/**
 * Shared hook to repair a project's GitHub `package.json` when the latest
 * AI generation slipped invalid deps (path aliases, "^latest", etc.) into
 * the manifest and the GitHub Actions build fails with EINVALIDPACKAGENAME
 * or EINVALIDTAGNAME at npm install. Backend sanitizes the manifest in
 * place and pushes a fix commit, which automatically triggers a fresh
 * build via the push webhook.
 */

import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../lib/api';

export interface RepairResult {
  changed: boolean;
  commitSha?: string;
  commitUrl?: string;
  stripped?: string[];
  added?: string[];
  message: string;
}

export function useRepairPackageJson(projectId: string) {
  return useMutation({
    mutationFn: async () => {
      const res = await api.post(`/builds/${projectId}/repair-package-json`);
      return res.data.data as RepairResult;
    },
    onSuccess: (data) => {
      if (!data.changed) {
        toast.info(data.message);
        return;
      }
      toast.success(
        <div className="text-xs">
          <strong>package.json repaired</strong>
          <div className="mt-1 text-gray-600">{data.message}</div>
          {data.stripped && data.stripped.length > 0 && (
            <div className="mt-1">Stripped: {data.stripped.length}</div>
          )}
          {data.added && data.added.length > 0 && <div>Added: {data.added.length}</div>}
        </div>,
        { duration: 6000 }
      );
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to repair package.json');
    },
  });
}
